import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AccessDeniedMessage from "@/components/errors/AccessDeniedMessage";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  type TabsValue,
} from "@/components/tabs/Tabs";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import {
  ReleaseFlag,
  useReleaseManagement,
} from "@/utility/feature-management";
import { retrieveValue } from "@/utility/storage";
import Scaler from "../dash/components/scaler/Scaler";
import DashPage from "../dash/DashPage";
import styles from "./styles";
import classes from "./styles.module.css";
import {
  type BookingTabKey,
  type BookingTabUrlValue,
  CORE_BOOKING_TABS,
  DEFAULT_BOOKING_TAB_URL_VALUE,
} from "./bookingTabs";
import { BookingsSettingsMenu } from "./BookingsSettingsMenu";
import { BookingTabContent } from "./BookingTabContent";
import { useBookingsPreference } from "./useBookingsPreference";
import { useVisitedTabs } from "./useVisitedTabs";

const tabListSx: SxProps<Theme> = {
  textAlign: "center",
  backgroundColor: "background.paper",
  minHeight: 36,
  "& .MuiTabs-flexContainer": {
    width: "max-content",
    minWidth: "100%",
    justifyContent: "center",
  },
  "& .MuiTabs-indicator": { display: "none" },
  "& .MuiTab-root": {
    whiteSpace: "nowrap",
    minWidth: "auto",
    minHeight: 36,
    padding: "6px 20px",
    color: "common.black",
    fontWeight: "bold",
    textTransform: "none",
    transition: (theme: Theme) =>
      theme.transitions.create("background-color", {
        duration: theme.transitions.duration.shortest,
      }),
  },
  "@media (hover: hover) and (pointer: fine)": {
    "& .MuiTab-root:hover": { backgroundColor: "action.hover" },
  },
  "& .MuiTab-root.Mui-selected": { color: "brand.main" },
};

const getSelectedTab = (
  requestedTab: string | null,
): (typeof CORE_BOOKING_TABS)[number] => {
  return (
    CORE_BOOKING_TABS.find((tab) => tab.urlValue === requestedTab) ??
    CORE_BOOKING_TABS.find(
      (tab) => tab.urlValue === DEFAULT_BOOKING_TAB_URL_VALUE,
    ) ??
    CORE_BOOKING_TABS[0]
  );
};

const withSelectedTab = (
  search: string,
  urlValue: BookingTabUrlValue,
): string => {
  const parameters = new URLSearchParams(search);
  if (urlValue === DEFAULT_BOOKING_TAB_URL_VALUE) {
    parameters.delete("tab");
  } else {
    parameters.set("tab", urlValue);
  }

  const nextSearch = parameters.toString();
  return nextSearch ? `?${nextSearch}` : "";
};

export const BookingsPageV2 = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { hasPermission } = useUserPermissions();
  const liveUpdateGatewayEnabled = useReleaseManagement(
    ReleaseFlag.releaseLiveUpdateGateway,
  );
  const bookingsLiveUpdatesEnabled = useReleaseManagement(
    ReleaseFlag.releaseBookingsPageV2LiveUpdates,
  );
  const liveUpdatesToggleEnabled = useReleaseManagement(
    ReleaseFlag.releaseBookingsPageV2LiveUpdatesToggle,
  );
  const liveUpdatesAvailable =
    liveUpdateGatewayEnabled && bookingsLiveUpdatesEnabled;
  const [liveUpdatesPreferred, setLiveUpdatesPreferred] = useBookingsPreference(
    "liveUpdates",
    true,
  );
  const liveUpdatesEnabled =
    liveUpdatesAvailable && (!liveUpdatesToggleEnabled || liveUpdatesPreferred);
  const queryClient = useQueryClient();
  const [animationsEnabled] = useState(
    () => retrieveValue("animations", "1") !== "0",
  );
  const canReadBookings = hasPermission("bookingManagement.booking.read");
  const requestedTab = new URLSearchParams(location.search).get("tab");
  const selectedTab = getSelectedTab(requestedTab);
  const visitedTabs = useVisitedTabs<BookingTabKey>();
  const animateSelectedTabEntrance =
    animationsEnabled && !visitedTabs.hasVisited(selectedTab.key);

  useEffect(() => {
    if (requestedTab != null && requestedTab !== selectedTab.urlValue) {
      history.replace({
        ...location,
        search: withSelectedTab(location.search, DEFAULT_BOOKING_TAB_URL_VALUE),
      });
    }
  }, [history, location, requestedTab, selectedTab.urlValue]);

  if (!canReadBookings) {
    return (
      <DashPage>
        <AccessDeniedMessage />
      </DashPage>
    );
  }

  const handleLiveUpdatesChange = (checked: boolean) => {
    setLiveUpdatesPreferred(checked);
    if (checked) {
      // Refetch data to catch up on changes missed while live updates were paused.
      void queryClient.invalidateQueries({ queryKey: ["bookings", "core"] });
    }
  };

  const handleSelectedTabChange = (nextValue: TabsValue) => {
    const nextTab = CORE_BOOKING_TABS.find((tab) => tab.key === nextValue);
    if (nextTab == null) {
      return;
    }

    history.replace({
      ...location,
      search: withSelectedTab(location.search, nextTab.urlValue),
    });
  };

  return (
    <DashPage>
      <div style={styles.wrapper}>
        <div style={styles.container}>
          <Box
            style={styles.toolBar}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: { xs: "wrap", sm: "nowrap" },
              gap: 1.5,
            }}
          >
            <Scaler>
              <div
                className={
                  animationsEnabled ? classes.toolbarEntering : undefined
                }
              >
                <h1 style={{ ...styles.title, margin: 0 }}>
                  {t("bookings.title")}
                </h1>
              </div>
            </Scaler>
            {liveUpdatesAvailable && liveUpdatesToggleEnabled && (
              <BookingsSettingsMenu
                liveUpdates={{
                  checked: liveUpdatesPreferred,
                  onChange: handleLiveUpdatesChange,
                }}
              />
            )}
          </Box>

          <Tabs
            value={selectedTab.key}
            onValueChange={handleSelectedTabChange}
            id="bookings"
          >
            <TabsList
              sx={tabListSx}
              aria-label={t("bookings.coreViewsLabel")}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {CORE_BOOKING_TABS.map((tab) => (
                <TabsTrigger key={tab.key} value={tab.key}>
                  {t(tab.titleKey)}
                </TabsTrigger>
              ))}
            </TabsList>

            {CORE_BOOKING_TABS.map((tab) => (
              <TabsContent
                key={tab.key}
                className={classes.tabContent}
                value={tab.key}
              >
                <BookingTabContent
                  tab={tab}
                  animationsEnabled={animationsEnabled}
                  animateInitialEntry={
                    tab.key === selectedTab.key && animateSelectedTabEntrance
                  }
                  onInitialEntryStarted={() => visitedTabs.markVisited(tab.key)}
                  liveUpdatesEnabled={liveUpdatesEnabled}
                />
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </DashPage>
  );
};
